// Sprint runner-ssot-unification PR-1 — authoritative usage cache store.
//
// INV-X (docs/architecture.md): every UI surface that renders runner
// capacity reads from this store, which polls `GET /api/runners/usage`
// (the S20.8 real-Anthropic-data cache). The sidebar pill, the modal,
// the RunnerChip tooltip — all read from the same Zustand slice.
//
// Lifecycle: `startRunnersUsagePolling()` is called once at the page
// mount (HomePage, next to `startRunnersPolling()`). Same
// visibility-aware pattern: hidden tabs skip the fetch.
//
// Sprint usage-single-emitter (D013-EC-166) — this poll is now
// `cacheOnly`: it reads the control-plane cache and can never cause an
// Anthropic fan-out. It was the DOMINANT emitter in the fleet, and it
// was never forced — its 60 s cadence deliberately "mirrored the backend
// cache TTL", which guaranteed a near-miss on almost every poll, i.e. a
// full fan-out roughly every minute, around the clock, whether or not
// anybody was looking. The cadence stays at 60 s because it is now free;
// what it buys is `fetchedAtIso` freshness on screen, not upstream reads.

import { create } from 'zustand';
import {
  fetchRunnersUsage,
  type RunnerUsageEntry,
} from '../lib/api';

const POLL_INTERVAL_MS = 60_000;

export interface RunnersUsageState {
  entriesByRunnerId: Record<string, RunnerUsageEntry>;
  loaded: boolean;
  error: string | null;
  // Sprint usage-single-emitter (D013-EC-166) — when the control-plane
  // cache last reached Anthropic. Every usage surface renders this as a
  // visible age, because after this sprint most reads are deliberately
  // cached and the founder must be able to tell fresh from stale.
  fetchedAtIso: string | null;

  // `bypassBackoff` (sprint usage-backoff-truth, D013-EC-148) is the
  // founder-clicked-just-now marker — see `fetchRunnersUsage`. Machine
  // callers pass `cacheOnly` instead and can never reach upstream.
  hydrate: (opts?: {
    fresh?: boolean;
    bypassBackoff?: boolean;
    cacheOnly?: boolean;
  }) => Promise<RunnerUsageEntry[]>;
  _setEntries: (entries: RunnerUsageEntry[]) => void;
  resetForTests: () => void;
}

export const useRunnersUsageStore = create<RunnersUsageState>((set) => ({
  entriesByRunnerId: {},
  loaded: false,
  error: null,
  fetchedAtIso: null,

  hydrate: async (opts) => {
    try {
      const res = await fetchRunnersUsage({
        fresh: opts?.fresh,
        bypassBackoff: opts?.bypassBackoff,
        cacheOnly: opts?.cacheOnly,
      });
      const map: Record<string, RunnerUsageEntry> = {};
      for (const r of res.runners) map[r.runnerId] = r;
      set({
        entriesByRunnerId: map,
        loaded: true,
        error: null,
        fetchedAtIso: res.fetchedAtIso ?? null,
      });
      return res.runners;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Could not load usage' });
      return [];
    }
  },

  _setEntries: (entries) =>
    set(() => {
      const map: Record<string, RunnerUsageEntry> = {};
      for (const r of entries) map[r.runnerId] = r;
      return { entriesByRunnerId: map, loaded: true, error: null };
    }),

  resetForTests: () =>
    set({ entriesByRunnerId: {}, loaded: false, error: null, fetchedAtIso: null }),
}));

export function startRunnersUsagePolling(intervalMs: number = POLL_INTERVAL_MS): () => void {
  void useRunnersUsageStore.getState().hydrate({ cacheOnly: true });
  const id = window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      void useRunnersUsageStore.getState().hydrate({ cacheOnly: true });
    }
  }, intervalMs);
  return () => window.clearInterval(id);
}
